import ExcelJS from "exceljs";
import { useTranslation } from "react-i18next";
import { useCuttingPlanStore } from "../../stores/cuttingPlanStore";
import { saveAs } from "file-saver";
import { calculateOverlapLength, calculateWeight } from "../../other/utilFunctions";
import { addWatermarkImage } from "./utilFunctions";

export default function useExportCuttingPlan() {
    const { t } = useTranslation();
    const allPatterns = useCuttingPlanStore((state) => state.allPatterns);
    const rebarInformation = useCuttingPlanStore((state) => state.rebarInformation);

    const projectOverlapRatio = useCuttingPlanStore((state) => state.cuttingPlanInformation.projectOverlapRatio);

    const cpName = useCuttingPlanStore((state) => state.cuttingPlanInformation.cpName);

    const workbook = new ExcelJS.Workbook();

    const cutsSheet = () => {
        const worksheet = workbook.addWorksheet(t("Kesimler"));

        const columnWidths = [10, 15, 15, 15, 15];
        const totalCols = columnWidths.length;

        // Set column widths
        columnWidths.forEach((width, i) => {
            worksheet.getColumn(i + 1).width = width;
        });

        // Add watermark image in merged top row
        addWatermarkImage(workbook, worksheet, totalCols);

        // Set headers in row 2
        const headers = [
            t("Kesim No"),
            t("Parça Sayısı"),
            t("Kesim Adedi"),
            `${t("Uzunluk")} (cm)`,
            `${t("Çap")} (mm)`,
        ];

        headers.forEach((header, colIndex) => {
            const cell = worksheet.getCell(2, colIndex + 1);
            cell.value = header;
            cell.font = { bold: true };
            cell.alignment = { horizontal: "center" };
        });

        // Add data rows starting from row 3
        const allCuts = allPatterns
            .filter((pattern) => !pattern.preCut)
            .flatMap((pattern) =>
                pattern.cuts.map((cut) => ({
                    patternNo: pattern.patternNo,
                    nLogs: pattern.nLogs,
                    nCuts: cut.nCuts,
                    cutLength: cut.cutLength,
                    diameter: pattern.diameter,
                }))
            );

        allCuts.forEach((rowData, index) => {
            const row = worksheet.getRow(3 + index);
            row.values = [
                rowData.patternNo,
                rowData.nCuts,
                rowData.nLogs,
                rowData.cutLength,
                rowData.diameter,
            ];
        });
    };

    const placementsSheet = () => {
        const worksheet = workbook.addWorksheet(t("Yerleşimler"));

        const columnWidths = [10, 10, 12, 15, 12, 15, 15, 15, 20, 15];
        const totalCols = columnWidths.length;
        // Set column widths
        columnWidths.forEach((width, i) => {
            worksheet.getColumn(i + 1).width = width;
        });
        // Add watermark image in merged top row
        addWatermarkImage(workbook, worksheet, totalCols);
        // Set headers in row 2
        const headers = [
            t("Kesim No"),
            t("Adet"),
            `${t("Uzunluk")} (cm)`,
            `${t("Çap")} (mm)`,
            t("Poz"),
            t("Donatı Kodu"),
            t("Kat"),
            t("Yapı Elemanı"),
            t("Yapı Elemanı Kodu"),
            t("Açıklama"),
        ];
        headers.forEach((header, colIndex) => {
            const cell = worksheet.getCell(2, colIndex + 1);
            cell.value = header;
            cell.font = { bold: true };
            cell.alignment = { horizontal: "center" };
        });

        // Add data rows starting from row 3
        const allPlacements = allPatterns
            .filter(pattern => !pattern.preCut)
            .map(pattern => {
                const allElements = Object.entries(pattern.elements);
                return allElements.map(element => ({
                    patternNo: pattern.patternNo,
                    count: pattern.nLogs * parseFloat(element[1]),
                    cutLength: element[0].split("-")[1],
                    diameter: pattern.diameter,
                    pose: rebarInformation[element[0]]["poz"],
                    rebarCode: rebarInformation[element[0]]["donatiKodu"],
                    floor: rebarInformation[element[0]]["kat"],
                    structureElement: rebarInformation[element[0]]["yapiElemani"],
                    structureElementCode: rebarInformation[element[0]]["yapiElemaniKodu"],
                    explanation: rebarInformation[element[0]]["aciklama"],
                }));
            }).flat();

        allPlacements.forEach((rowData, index) => {
            const row = worksheet.getRow(3 + index);
            row.values = [
                rowData.patternNo,
                rowData.count,
                rowData.cutLength,
                rowData.diameter,
                rowData.pose,
                rowData.rebarCode,
                rowData.floor,
                rowData.structureElement,
                rowData.structureElementCode,
                rowData.explanation,
            ];
        });
    };

    const substitutionsSheet = () => {
        const worksheet = workbook.addWorksheet(t("Yerine Kullanımlar"));

        const columnWidths = [10, 10, 12, 15, 12, 15, 15, 15, 20, 25];
        const totalCols = columnWidths.length;
        // Set column widths
        columnWidths.forEach((width, i) => {
            worksheet.getColumn(i + 1).width = width;
        });
        // Add watermark image in merged top row
        addWatermarkImage(workbook, worksheet, totalCols);
        // Set headers in row 2
        const headers = [
            t("Kesim No"),
            t("Adet"),
            `${t("Uzunluk")} (cm)`,
            `${t("Çap")} (mm)`,
            t("Poz"),
            t("Yerine Kullanılan"),
            t("Kat"),
            t("Yapı Elemanı"),
            t("Yapı Elemanı Kodu"),
            t("Açıklama"),
        ];
        headers.forEach((header, colIndex) => {
            const cell = worksheet.getCell(2, colIndex + 1);
            cell.value = header;
            cell.font = { bold: true };
            cell.alignment = { horizontal: "center" };
        });

        // Add data rows starting from row 3
        const allSubstitutions = allPatterns
            .filter(pattern => "substitutions" in pattern)
            .map(pattern => {
                const substitutionsOfPattern = pattern.substitutions;
                return substitutionsOfPattern.map(substitution => ({
                    patternNo: pattern.patternNo,
                    count: substitution.number,
                    cutLength: substitution.subs_by,
                    diameter: pattern.diameter,
                    pose: rebarInformation[substitution.actual]["poz"],
                    actual: substitution.actual,
                    floor: rebarInformation[substitution.actual]["kat"],
                    structureElement: rebarInformation[substitution.actual]["yapiElemani"],
                    structureElementCode: rebarInformation[substitution.actual]["yapiElemaniKodu"],
                    explanation: rebarInformation[substitution.actual]["aciklama"],
                }))
            }).flat();

        allSubstitutions.forEach((rowData, index) => {
            const row = worksheet.getRow(3 + index);
            row.values = [
                rowData.patternNo,
                rowData.count,
                rowData.cutLength,
                rowData.diameter,
                rowData.pose,
                rowData.actual,
                rowData.floor,
                rowData.structureElement,
                rowData.structureElementCode,
                rowData.explanation,
            ];
        });
    };

    const wastesSheet = () => {
        const worksheet = workbook.addWorksheet(t("Fireler"));

        const columnWidths = [10, 15, 15, 17];
        const totalCols = columnWidths.length;
        // Set column widths
        columnWidths.forEach((width, i) => {
            worksheet.getColumn(i + 1).width = width;
        });
        // Add watermark image in merged top row
        addWatermarkImage(workbook, worksheet, totalCols);
        // Set headers in row 2
        const headers = [
            t("Kesim No"),
            t("Adet"),
            `${t("Uzunluk")} (cm)`,
            `${t("Çap")} (mm)`,
        ];
        headers.forEach((header, colIndex) => {
            const cell = worksheet.getCell(2, colIndex + 1);
            cell.value = header;
            cell.font = { bold: true };
            cell.alignment = { horizontal: "center" };
        });

        // Add data rows starting from row 3
        const allWastes = allPatterns
            .filter(pattern => !pattern.preCut)
            .filter(pattern => pattern.waste > 0)
            .map(pattern => ({
                patternNo: pattern.patternNo,
                nLogs: pattern.nLogs,
                waste: pattern.waste,
                diameter: pattern.diameter,
            }));

        allWastes.forEach((rowData, index) => {
            const row = worksheet.getRow(3 + index);
            row.values = [
                rowData.patternNo,
                rowData.nLogs,
                rowData.waste,
                rowData.diameter,
            ];
        });
    };

    const longRebarsSheet = () => {
        const worksheet = workbook.addWorksheet(t("Uzun Donatılar"));
        const columnWidths = [10, 10, 15, 20, 15, 15, 18, 25];
        const totalCols = columnWidths.length;
        // Set column widths
        columnWidths.forEach((width, i) => {
            worksheet.getColumn(i + 1).width = width;
        });
        // Add watermark image in merged top row
        addWatermarkImage(workbook, worksheet, totalCols);
        // Set headers in row 2
        const headers = [
            t("Kesim No"),
            t("Adet"),
            `${t("Çap")} (mm)`,
            `${t("Kesilen Uzunluk")} (cm)`,
            `${t("Asıl Uzunluk")} (cm)`,
            t("Donatı Kodu"),
            `${t("Bindirme Boyu")} (cm)`,
            t("Diğer Parça Kodları"),
        ];
        headers.forEach((header, colIndex) => {
            const cell = worksheet.getCell(2, colIndex + 1);
            cell.value = header;
            cell.font = { bold: true };
            cell.alignment = { horizontal: "center" };
        });
        // Add data rows starting from row 3
        const allLongRebars = allPatterns
            .filter(pattern => {
                const elements = Object.keys(pattern.elements);
                return elements.some(element =>
                    "kesikParcaKodlari" in rebarInformation[element]
                );
            })
            .map(pattern => {
                const longElements = Object.keys(pattern.elements)
                    .filter(element =>
                        "kesikParcaKodlari" in rebarInformation[element]
                    )
                    .map(element => ({
                        patternNo: pattern.patternNo,
                        diameter: pattern.diameter,
                        count: pattern.elements[element],
                        length: element.split("-")[1],
                        rebarCode: rebarInformation[element]["donatiKodu"],
                        overlapLength: calculateOverlapLength(
                            parseFloat(pattern.diameter),
                            parseFloat(projectOverlapRatio)
                        ),
                        totalLength: rebarInformation[element]["toplamUzunluk"],
                        otherPieceCodes: rebarInformation[element]["kesikParcaKodlari"],
                    }));
                return longElements;
            }).flat();

        let startRowIndex;
        let endRowIndex = 2;

        allLongRebars.forEach((rowData, index) => {
            const { patternNo, count, diameter, length, totalLength, rebarCode, overlapLength, otherPieceCodes } = rowData;

            startRowIndex = endRowIndex +  1;
            endRowIndex = startRowIndex + otherPieceCodes.length - 1;

            const startRow = worksheet.getRow(startRowIndex);

            startRow.values = [
                patternNo,
                count,
                diameter,
                length,
                totalLength,
                rebarCode,
                overlapLength,
                otherPieceCodes[0],
            ];

            // Add additional rows for the remaining elements of otherPieceCodes
            for (let i = 1; i < otherPieceCodes.length; i++) {
                worksheet.getRow(startRowIndex + i).values = [
                    "",
                    "",
                    "",
                    "",
                    "",
                    "",
                    "",
                    otherPieceCodes[i],
                ];
            }

            // Merge cells for the other fields
            if (otherPieceCodes.length > 1) {
                worksheet.mergeCells(`A${startRowIndex}:A${endRowIndex}`); // Merge "Kesim No"
                worksheet.mergeCells(`B${startRowIndex}:B${endRowIndex}`); // Merge "Adet"
                worksheet.mergeCells(`C${startRowIndex}:C${endRowIndex}`); // Merge "Çap"
                worksheet.mergeCells(`D${startRowIndex}:D${endRowIndex}`); // Merge "Kesilen Uzunluk"
                worksheet.mergeCells(`E${startRowIndex}:E${endRowIndex}`); // Merge "Asıl Uzunluk"
                worksheet.mergeCells(`F${startRowIndex}:F${endRowIndex}`); // Merge "Donatı Kodu"
                worksheet.mergeCells(`G${startRowIndex}:G${endRowIndex}`); // Merge "Bindirme Boyu"
            }
        });
    };

    const precutRebarPlacementsSheet = () => {
        const worksheet = workbook.addWorksheet(t("Hazır Kesimli Yerleşimler"));
        const columnWidths = [10, 10, 12, 15, 12, 15, 15, 15, 20, 15, 17];
        const totalCols = columnWidths.length;
        // Set column widths
        columnWidths.forEach((width, i) => {
            worksheet.getColumn(i + 1).width = width;
        });
        // Add watermark image in merged top row
        addWatermarkImage(workbook, worksheet, totalCols);
        // Set headers in row 2
        const headers = [
            t("Kesim No"),
            t("Adet"),
            `${t("Uzunluk")} (cm)`,
            `${t("Çap")} (mm)`,
            t("Poz"),
            t("Donatı Kodu"),
            t("Kat"),
            t("Yapı Elemanı"),
            t("Yapı Elemanı Kodu"),
            t("Açıklama"),
            `${t("Ağırlık")} (kg)`,
        ];
        headers.forEach((header, colIndex) => {
            const cell = worksheet.getCell(2, colIndex + 1);
            cell.value = header;
            cell.font = { bold: true };
            cell.alignment = { horizontal: "center" };
        });
        // Add data rows starting from row 3
        const preCutRebarsNonGrouped = allPatterns
            .filter(pattern => pattern.preCut)
            .map((pattern, i) => {
                return Object.entries(pattern.elements).map((element, j) => ({
                    diameter: pattern.diameter,
                    element: element[0],
                    count: element[1],
                    cutLength: element[0].split("-")[1],
                    patternNo: pattern.patternNo,
                    floor: rebarInformation[element[0]]["kat"],
                    structureElement: rebarInformation[element[0]]["yapiElemani"],
                    structureElementCode: rebarInformation[element[0]]["yapiElemaniKodu"],
                    explanation: rebarInformation[element[0]]["aciklama"],
                    pose: rebarInformation[element[0]]["poz"],
                    rebarCode: rebarInformation[element[0]]["donatiKodu"],
                    weight: calculateWeight(
                        parseFloat(pattern.diameter),
                        parseFloat(element[1]) * parseFloat(element[0].split("-")[1])
                    ).toFixed(1),
                }));
            }).flat();

        const preCutRebars = preCutRebarsNonGrouped.reduce((acc, row) => {
            const existingRow = acc.find(r => r.rebarCode === row.rebarCode);
            if (existingRow) {
                existingRow.count += row.count;
                existingRow.weight = (parseFloat(existingRow.weight) + parseFloat(row.weight)).toFixed(1);
            }
            else {
                acc.push(row);
            }
            return acc;
        }, []);

        const totalWeight = preCutRebars.reduce((acc, row) => {
            const weight = parseFloat(row.weight);
            return acc + (isNaN(weight) ? 0 : weight);
        }, 0);

        preCutRebars.forEach((rowData, index) => {
            const row = worksheet.getRow(3 + index);
            row.values = [
                rowData.patternNo,
                rowData.count,
                rowData.cutLength,
                rowData.diameter,
                rowData.pose,
                rowData.rebarCode,
                rowData.floor,
                rowData.structureElement,
                rowData.structureElementCode,
                rowData.explanation,
                rowData.weight,
            ];
        });

        // Add empty row for spacing
        worksheet.addRow({});

        // Add total weight at the very end of the worksheet
        const totalWeightRowIndex = 3 + preCutRebars.length + 1;
        const totalWeightRow = worksheet.getRow(totalWeightRowIndex);
        totalWeightRow.values = [
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            t("Toplam Ağırlık") + " (kg)",
            totalWeight,
        ];
        totalWeightRow.getCell(10).font = { bold: true };
        totalWeightRow.getCell(11).font = { bold: true };
    };

    const precutRebarPiecesSheet = () => {
        const worksheet = workbook.addWorksheet(t("Hazır Kesimli Donatılar"));
        const columnWidths = [10, 15, 15, 15];
        const totalCols = columnWidths.length;
        // Set column widths
        columnWidths.forEach((width, i) => {
            worksheet.getColumn(i + 1).width = width;
        });

        // Add watermark image in merged top row
        addWatermarkImage(workbook, worksheet, totalCols);

        // Set headers in row 2
        const headers = [
            `${t("Çap")} (mm)`,
            t("Adet"),
            `${t("Uzunluk")} (cm)`,
            `${t("Ağırlık")} (kg)`,
        ];

        headers.forEach((header, colIndex) => {
            const cell = worksheet.getCell(2, colIndex + 1);
            cell.value = header;
            cell.font = { bold: true };
            cell.alignment = { horizontal: "center" };
        });

        // Add data rows starting from row 3
        const preCutRebarsNonGrouped = allPatterns
            .filter(pattern => pattern.preCut)
            .map((pattern, i) => {
                return Object.entries(pattern.elements).map((element, j) => ({
                    diameter: pattern.diameter,
                    element: element[0],
                    count: element[1],
                    cutLength: element[0].split("-")[1],
                    weight: calculateWeight(
                        parseFloat(pattern.diameter),
                        parseFloat(element[1]) * parseFloat(element[0].split("-")[1])
                    ).toFixed(1),
                }));
            }).flat();

        const preCutRebars = preCutRebarsNonGrouped.reduce((acc, row) => {
            const existingRow = acc.find(r => r.diameter === row.diameter && r.cutLength === row.cutLength);
            if (existingRow) {
                existingRow.count += row.count;
                existingRow.weight = (parseFloat(existingRow.weight) + parseFloat(row.weight)).toFixed(1);
            }
            else {
                acc.push(row);
            }
            return acc;
        }, []);

        const totalWeight = preCutRebars.reduce((acc, row) => {
            const weight = parseFloat(row.weight);
            return acc + (isNaN(weight) ? 0 : weight);
        }, 0);

        preCutRebars.forEach((rowData, index) => {
            const row = worksheet.getRow(3 + index);
            row.values = [
                rowData.diameter,
                rowData.count,
                rowData.cutLength,
                rowData.weight,
            ];
        });

        // Add empty row for spacing
        worksheet.addRow({});
        // Add total weight at the very end of the worksheet
        const totalWeightRowIndex = 3 + preCutRebars.length + 1;
        const totalWeightRow = worksheet.getRow(totalWeightRowIndex);
        totalWeightRow.values = [
            "",
            "",
            t("Toplam Ağırlık") + " (kg)",
            totalWeight,
        ];
        totalWeightRow.getCell(3).font = { bold: true };
        totalWeightRow.getCell(4).font = { bold: true };
    };

    const clearWorkbook = () => {
        workbook.removeWorksheet(t("Kesimler"));
        workbook.removeWorksheet(t("Yerleşimler"));
        workbook.removeWorksheet(t("Yerine Kullanımlar"));
        workbook.removeWorksheet(t("Fireler"));
        workbook.removeWorksheet(t("Uzun Donatılar"));
        workbook.removeWorksheet(t("Hazır Kesimli Yerleşimler"));
        workbook.removeWorksheet(t("Hazır Kesimli Donatılar"));
    };

    const exportCuttingPlan = () => {
        cutsSheet();
        placementsSheet();
        substitutionsSheet();
        wastesSheet();
        longRebarsSheet();
        precutRebarPlacementsSheet();
        precutRebarPiecesSheet();

        const fileName = `${cpName}_${new Date().toISOString().replace(/[:.-]/g, "")}.xlsx`;

        workbook.xlsx.writeBuffer().then((data) => {
            const blob = new Blob([data], { type: "application/octet-stream" });
            saveAs(blob, fileName);
        });

        clearWorkbook();
    };

    return { exportCuttingPlan };
}